<script setup lang="ts">
import { onClickOutside } from "@vueuse/core";
import { watch, ref, useTemplateRef } from "vue";

import { TDropButtonPosition } from "@/components/ui/buttons/drop-button.types";

interface IProps {
  right?: boolean;
  position?: TDropButtonPosition;
}

interface IEmits {
  (e: "show"): void;
  (e: "hide"): void;
}

const emits = defineEmits<IEmits>();
defineSlots<{
  trigger: () => unknown;
  content(props: { hideContent: () => void }): void;
}>();

withDefaults(defineProps<IProps>(), {
  right: false,
  position: "bottom",
});

const showContent = ref<boolean>(false);
const button = useTemplateRef("dropButton");

function hideContent() {
  showContent.value = false;
}

watch(showContent, (newValue) => {
  if (newValue) {
    emits("show");
    return;
  }

  emits("hide");
});

onClickOutside(button, () => {
  hideContent();
});
</script>

<template>
  <div
    ref="dropButton"
    class="drop-button relative"
  >
    <div
      aria-controls="drop-button-box"
      :aria-expanded="showContent"
      aria-label="drop-button-trigger"
      class="drop-button_trigger"
      @click.stop="showContent = !showContent"
    >
      <slot name="trigger" />
    </div>
    <div
      v-if="showContent"
      id="drop-button-box"
      class="drop-button_box border bg-white rounded-xl p-1 absolute mt-1 z-10"
      :class="{
        'right-0': $props.right,
        'top-0 right-12': $props.position === 'left',
      }"
    >
      <slot
        name="content"
        :hideContent="hideContent"
      />
    </div>
  </div>
</template>
